/* -----------------------------------------------------------------------------------------------------
  - Created: 03-03-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains Destination table,add/edit form
  -------------------------------------------------------------------------------------------------------
*/

import React, { useEffect, useState } from 'react';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal';
import { ErrorToast, InfoToast, SuccessToast } from '../../../../utils/toast';
import CustomDeleteConfirmModal from '../../../../Components/custom_components/CustomDeleteConfirmModal/CustomDeleteConfirmModal';
import DesignationForm from '../DesignationForm/DesignationForm';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import DesignationSearchFilter from '../DesignationSearchFilter/DesignationSearchFilter';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { designation_creation, designation_deletion, designation_update, get_designation_by_id, get_designation_with_pagination } from '../../../../config/SettingsApis/designationApis';
import { useSelector } from 'react-redux';
import { hasPrivilege } from '../../../../utils/privileges';
import Loader from '../../../../Components/Loader/Loader';
import { Icons } from '../../../../utils/GlobalIcons';
import './DesignationTableStyle.css';
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder';
import { getStatusColors } from '../../../../utils/constants';

function DesignationTable() {
    const privileges = useSelector((state) => state.privileges.privileges);

    const [pageNumber, setPageNumber] = useState(1); // Current page number
    const [totalItemPerPage, setTotalItemPerPage] = useState(25); // Items per page
    const [totalPages, setTotalPages] = useState(0);  // Total number of pages
    const [destinationsListing, setDestinationsListing] = useState([]); // Destination list
    const [refresh, setRefresh] = useState(false); // State for refreshing data
    const [isButtonDisabled, setIsButtonDisabled] = useState(false); // Button disable state

    // Variables for both modals
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({ isOpen: false, id: '' });
    const [modalProps, setModalProps] = useState({ isOpen: false, id: '', formValues: {} });
    const [isLoading, setIsLoading] = useState(false); // Loader state

    // Pagination state
    const [pagination, setPagination] = useState({
        start: 0,
        end: 0,
        totalEntries: 0,
    });

    useEffect(() => {
        get_destinations();
    }, [pageNumber, totalItemPerPage, refresh]);

    const get_destinations = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItem_count', totalItemPerPage);
        try {
            setIsLoading(true)
            const response = await get_designation_with_pagination(formData);
            if (response?.status === 'success') {
                setDestinationsListing(response.data.data);
                setTotalPages(response.data.last_page);
                const updatedPagination = handlePaginationEntries(response.data.from, response.data.to, response.data.total)
                setPagination(updatedPagination);
            } else {
                setDestinationsListing([]);
            }
        } catch (error) {
            setDestinationsListing([]);
        } finally {
            setIsLoading(false)
        }
    };



    // Map destination data to table rows
    const datas = () =>
        destinationsListing?.map((item, index) => ({
            key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
            id: item.DesignationId,
            designation: item.Designation_Name,
            status: item?.Designation_Status,
            DesignationDescription: item?.Designation_Description,

        }));

    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Settings", 11, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Settings", 11, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Settings", 11, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Settings", 11, PERMISSION_ACTIONS.add),
    };


    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;

    // Columns definition
    const columns = [
        { title: '#', key: 'key' },
        { title: 'Designation', key: 'designation' },
        {
            title: "Description",
            key: "DesignationDescription",
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.status || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },
        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false

    // Render custom actions based on user role
    const renderActions = (row) => {
        return (
            <div className='action-container'>
                {/* <FaEye className="view-action-icon" onClick={() => handleViewModal(true)} /> */}
                {userPermissions.edit &&
                    <CustomTooltipWithoutRoundBorder title="Edit">
                        <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                    </CustomTooltipWithoutRoundBorder>
                }
                {/* <FaTrashAlt className="delete-action-icon" onClick={() => handlDeleteModal(true, row?.id)} /> */}
            </div>

        );
    };
    // Handles opening and closing the form modal
    const handleModal = async (isOpen, designationID) => {
        if (designationID) {
            const params = { designationID: designationID };
            getDestinationById(params);
        } else {
            setModalProps({
                isOpen,
                id: '',
                formValues: { DesignationName: '', DesignationStatus: 1, Designation_Description: '' },
            });
        }
    };

    // GET DESTINATION BY ID
    const getDestinationById = async (params) => {
        try {
            const response = await get_designation_by_id(params);

            if (response?.status === 'success') {
                setModalProps({
                    isOpen: true,
                    id: response?.data?.DesignationId,
                    formValues: {
                        DesignationName: response?.data?.DesignationName,
                        DesignationStatus: response?.data?.DesignationStatus,
                        DesignationDescription: response?.data?.DesignationDescription || "",

                    },
                });
            } else {
                ErrorToast(response?.message || 'Something Went Wrong!');
            }
        } catch (error) {
            ErrorToast('Something Went Wrong!');
        }
    };

    const handleDeleteModal = (isOpen, id = '') => {
        setIsDeleteModalOpen({ isOpen, id });
    };

    // FOR CREATE /UPDATE DESTINATION
    const handleDesignationSave = async (values, designationID) => {
        const formData = new FormData();
        for (const key in values) {
            formData.append(key, values[key]);
        }

        try {
            setIsButtonDisabled(true);
            if (designationID) formData.append('designationID', designationID);

            const apiFunction = designationID ? designation_update : designation_creation;
            const response = await apiFunction(formData);

            const message = response?.data?.message || 'Something went wrong!';
            const status = response?.data?.status;

            if (status === 'success') {
                SuccessToast(message);
                handleModal(false);
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
            let errorMessage = 'Something went wrong!';
            // Check if error response contains dynamic errors
            if (error?.data?.errors) {
                for (const field in error.data.errors) {
                    if (error.data.errors[field] && error.data.errors[field].length > 0) {
                        errorMessage = error.data.errors[field][0];
                        break;  // want to stop after the first error field
                    }
                }
            }

            ErrorToast(errorMessage);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // FOR DELETE DESTINATION
    const handleDeleteDestination = async () => {
        const params = { designationId: isDeleteModalOpen?.id };
        try {
            const response = await designation_deletion(params);
            if (response?.data?.status === 'success') {
                SuccessToast(response?.data?.message);
                handleDeleteModal(false, '');
                setRefresh(!refresh);
            } else {
                ErrorToast(response?.data?.message || 'Something Went Wrong!');
            }
        } catch (error) {
            ErrorToast('Something Went Wrong!');
        }
    };

    // Update pagination
    const updatePagination = (pageNumber) => setPageNumber(pageNumber);
    const updateItemPageCount = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    return (
        <>
            <div className="designation-table-container">
                <DesignationSearchFilter
                    tableTitle="Designation "
                    isAddEdiModalOpen={modalProps.isOpen}
                    handleAddEditModal={handleModal}
                    isAddPermission={userPermissions?.add}

                />
                <CustomTable columns={columns} rows={isLoading ? [] : datas()} isLoading={isLoading} />

                {isLoading ? <Loader /> : <CustomBottomPagination
                    items_per_page={totalItemPerPage} // Total number of entries
                    current_page={pageNumber}   // The current active page
                    total_page_count={totalPages}  // Total number of pages
                    on_change_value={updatePagination} // Callback for page change
                    on_page_count_update={updateItemPageCount}   // Callback for items per page change
                    pagination={pagination} //FOR PAGINATION ENTRIES

                />}
            </div>

            {/* Add/Edit Modal */}
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={() => handleModal(false)}
                title={modalProps.id !== '' ? 'Edit Designation' : 'Create Designation'}
                body={
                    <DesignationForm
                        isButtonDisabled={isButtonDisabled}
                        handleDesignationSave={handleDesignationSave}
                        id={modalProps.id}
                        formIntialValues={modalProps.formValues}
                    />
                }
                maxWidth="1000px"
            />}


            {/* Delete Modal */}
            {isDeleteModalOpen?.isOpen && <CustomDeleteConfirmModal
                module_name="Designation"
                open_modal={isDeleteModalOpen?.isOpen}
                close_modal={handleDeleteModal}
                confirm_function={handleDeleteDestination}
            />}
        </>
    );
}

export default DesignationTable;
